import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  alpha,
  Box,
  Button,
  darken,
  hexToRgb,
  Pagination,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import { Typography } from "@platform/design-system";
import ExcelJS, { CellValue } from "exceljs";
import React, { useState } from "react";
import * as XLSX from "xlsx";

import {
  ProcessCensusFileResponse as CensusResponse,
  DataError,
  HeaderError,
} from "../types";

import { capitalizeFirstLetter, downloadExcelFile } from "./utils";

export enum ErrorStatus {
  HeaderError = "header-error",
  MissingWorkSheet = "missing-worksheet",
  MultipleWorksheets = "multiple-worksheets",
  NotValidFile = "not-valid-file",
  MissingDataRow = "missing-data-row",
  DataError = "data-error",
  MappingError = "mapping-error",
  MissingHeaderRow = "missing-header-row",
  OnProcessedError = "on-processed-error",
  FirstRowInvalidRelation = "first-row-invalid-relationship",
  MissingCoverages = "missing-coverages",
}

interface IErrorData {
  title: string;
  description: React.ReactNode;
}

const ErrorBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  backgroundColor: alpha(hexToRgb(theme.palette.dropzone.error), 0.1),
  color: darken(theme.palette.dropzone.error, 0.6),
}));

export const ErrorInfo = styled(Box)(({ theme }: { theme: Theme }) => ({
  paddingLeft: theme.spacing(2),
  display: "flex",
  flex: "1",
  flexDirection: "column",
}));

export const UlStyled = styled("ul")(({ theme }) => ({
  listStyleType: "disc",
  color: darken(theme.palette.dropzone.error, 0.6),
  fontSize: "14px",
  paddingLeft: "20px",
}));

interface CensusErrorProps {
  censusError: CensusResponse;
  file: File;
}

const CensusError = ({
  censusError,
  file,
}: CensusErrorProps): React.JSX.Element => {
  const theme = useTheme();

  const getErrorData = (response: CensusResponse): IErrorData => {
    switch (response.status) {
      case ErrorStatus.MissingWorkSheet:
        return {
          title: "Missing Worksheet",
          description: null,
        };
      case ErrorStatus.MissingDataRow:
        return {
          title: "Missing Census Data",
          description: "Unable to find any census data below the headers.",
        };
      case ErrorStatus.NotValidFile:
        return {
          title: "Not Valid File",
          description: null,
        };
      case ErrorStatus.MissingHeaderRow:
        return {
          title: "Missing Headers Row",
          description: "Unable to find headers in the indicated row.",
        };
      case ErrorStatus.MultipleWorksheets:
        return {
          title: "Multiple sheets are not supported",
          description: (
            <>
              <Typography
                variant="body-medium"
                color="inherit"
                component="span"
              >
                <strong>{file.name}</strong>
              </Typography>{" "}
              has more than one sheet. Please upload a document with only one
              sheet.
            </>
          ),
        };
      case ErrorStatus.OnProcessedError:
        return {
          title: "Error Processing File",
          description: response.message,
        };
      case ErrorStatus.FirstRowInvalidRelation:
        return {
          title: "Relationship Structure Incorrect",
          description:
            "The first row in the relationship column must be for an Employee",
        };
      default:
        return {
          title: "File could not be processed",
          description:
            "Please confirm that you uploadeded the correct file and try again.",
        };
    }
  };

  if (censusError.status === ErrorStatus.HeaderError) {
    const missingColumns: string[] = [];
    const duplicateColumns: HeaderError[] = [];
    for (const item of censusError.errors ?? []) {
      if (item.reason === "missing") {
        missingColumns.push(item.column);
      } else {
        duplicateColumns.push(item);
      }
    }

    return (
      <>
        {missingColumns.length > 0 && (
          <ErrorBox mt={3}>
            <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
            <ErrorInfo>
              <Typography color="inherit">
                <strong>Missing Headers</strong>
              </Typography>
              <Typography variant="body-medium" color="inherit" component="p">
                <Typography
                  variant="body-medium"
                  color="inherit"
                  component="span"
                >
                  <strong>{file?.name}</strong>
                </Typography>{" "}
                has {missingColumns.length} missing headers:
              </Typography>
              <Box>
                <UlStyled>
                  {missingColumns.map((item) => (
                    <li key={item}>
                      <Typography
                        variant="body-medium"
                        color="inherit"
                        component="span"
                      >
                        {capitalizeFirstLetter(item)}
                      </Typography>
                    </li>
                  ))}
                </UlStyled>
              </Box>
            </ErrorInfo>
          </ErrorBox>
        )}
        {duplicateColumns.length > 0 && (
          <ErrorBox mt={3}>
            <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
            <ErrorInfo>
              <Typography color="inherit">
                <strong>Duplicate Headers</strong>
              </Typography>
              <Typography variant="body-medium" color="inherit" component="p">
                <Typography
                  variant="body-medium"
                  color="inherit"
                  component="span"
                >
                  <strong>{file?.name}</strong>
                </Typography>{" "}
                has duplicate headers. Please remove or rename the following
                column headers:
              </Typography>
              <Box>
                <UlStyled>
                  {duplicateColumns.map((item: HeaderError) => (
                    <li key={item.column}>
                      <Typography
                        variant="body-medium"
                        color="inherit"
                        component="span"
                      >
                        {capitalizeFirstLetter(item.column)}
                      </Typography>
                      <UlStyled>
                        {item.values.map(
                          (value: CellValue) =>
                            typeof value === "string" && (
                              <li key={value}>
                                <Typography
                                  variant="body-medium"
                                  color="inherit"
                                  component="span"
                                >
                                  {capitalizeFirstLetter(value)}
                                </Typography>
                              </li>
                            ),
                        )}
                      </UlStyled>
                    </li>
                  ))}
                </UlStyled>
              </Box>
            </ErrorInfo>
          </ErrorBox>
        )}
      </>
    );
  }

  if (censusError.status === ErrorStatus.MappingError) {
    if (censusError.type === "classes") {
      return (
        <MappingError
          title="Class Descriptions Not Mapped"
          footer="Please map the Class Descriptions in the census to a Class Number in the above Company Details section."
          missingFromFormDescription="The following Class Descriptions are in the census but are not mapped to a Class Number:"
          missingFromCensusDescription="The following Classes in the above Company Details section are not found in the census:"
          missingFromForm={censusError.missing.form}
          missingFromCensus={censusError.missing.census}
          errorColor={theme.palette.dropzone.error}
        />
      );
    }
    if (censusError.type === "coverage-classes") {
      return (
        <MappingError
          title="Coverage Classes Not Mapped"
          footer="Please ensure all Classes within the Class Manager are associated to a Coverage Column within the Census file."
          missingFromFormDescription="The following Class(es) were detected in the census file, but are not mapped in the Class Manager:"
          missingFromCensusDescription="The following Class(es) are identified in the Class Manager, but not found in the census file:"
          missingFromForm={censusError.missing.form}
          missingFromCensus={censusError.missing.census}
          errorColor={theme.palette.dropzone.error}
        />
      );
    }
    const planType = capitalizeFirstLetter(censusError.type);
    return (
      <MappingError
        title="Plan Names Not Mapped"
        footer={`Please map the Plan Names in the census to a Plan in the above ${planType} Coverage section.`}
        missingFromFormDescription="The following Plan Names are in the census but are not mapped to a Plan:"
        missingFromCensusDescription={`The following Plan Names in the above ${planType} Coverage section are not found in the census:`}
        missingFromForm={censusError.missing.form}
        missingFromCensus={censusError.missing.census}
        errorColor={theme.palette.dropzone.error}
      />
    );
  }
  if (censusError.status === ErrorStatus.DataError) {
    const expandedErrors = getExpandedDataErrors(censusError.errors);
    return (
      <>
        <ErrorBox mt={3}>
          <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
          <ErrorInfo>
            <Typography color="inherit">
              <strong>Incorrect Fields</strong>
            </Typography>
            <Typography variant="body-medium" color="inherit" component="p">
              <Typography
                variant="body-medium"
                color="inherit"
                component="span"
              >
                <strong>{file?.name}</strong>
              </Typography>{" "}
              has {expandedErrors.length} incorrect fields
            </Typography>
          </ErrorInfo>
          <Button
            onClick={() =>
              handleExportDataErrors({ file, data: expandedErrors })
            }
            color="brandPrimary"
            startIcon={<DownloadOutlinedIcon />}
            variant="outlined"
            size="small"
            sx={{
              border: "1.5px solid",
              fontWeight: "bold",
              height: "50%",
            }}
          >
            Export Errors
          </Button>
        </ErrorBox>
        <ErrorsTable data={expandedErrors} />
      </>
    );
  }

  if (censusError.status === ErrorStatus.MissingCoverages) {
    return (
      <ErrorBox mt={3}>
        <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
        <ErrorInfo>
          <Typography color="inherit">
            <strong>Missing Coverages</strong>
          </Typography>
          <Typography variant="body-medium" color="inherit" component="p">
            <Typography variant="body-medium" color="inherit" component="span">
              <strong>{file?.name}</strong>
            </Typography>{" "}
            is missing quoted coverages:
          </Typography>
          <Box>
            <UlStyled>
              {censusError.errors.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key -- a fixed list of error messages, never reordered or edited
                <li key={index}>
                  <Typography
                    variant="body-medium"
                    color="inherit"
                    component="span"
                  >
                    The census member with coverages in row(s){" "}
                    {item.rowIndexes.join(", ")} is missing benefit row(s) for{" "}
                    {item.coverages.join(", ")}
                  </Typography>
                </li>
              ))}
            </UlStyled>
          </Box>
        </ErrorInfo>
      </ErrorBox>
    );
  }

  const { title, description }: IErrorData = getErrorData(censusError);

  return (
    <ErrorBox mt={3}>
      <ErrorOutlineIcon sx={{ color: theme.palette.dropzone.error }} />
      <ErrorInfo>
        <Typography color="inherit">
          <strong>{title}</strong>
        </Typography>
        {description ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body-medium" color="inherit" component="p">
              {description}
            </Typography>
          </Stack>
        ) : null}
      </ErrorInfo>
    </ErrorBox>
  );
};

export default CensusError;

const ErrorsTable = ({
  data,
}: {
  data: ExpandedDataError[];
}): React.JSX.Element => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const offset = currentPage * rowsPerPage;
  const limit = offset + rowsPerPage;
  return (
    <Table>
      {data?.length ? (
        <>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ fontWeight: 600 }}>
                Field Name
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 600 }}>
                Cell
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 600 }}>
                Reason
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(offset, limit)
              .map((item: ExpandedDataError, index: number) => (
                // eslint-disable-next-line react/no-array-index-key -- rows of a read-only error report, never reordered
                <TableRow key={index}>
                  <TableCell align="left">{item.column}</TableCell>
                  <TableCell align="left">{item.location}</TableCell>
                  <TableCell align="left">{item.message}</TableCell>
                </TableRow>
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <ErrorsTablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={data.length}
                page={currentPage}
                rowsPerPage={rowsPerPage}
                setPage={setCurrentPage}
                setRowsPerPage={setRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </>
      ) : (
        <TableBody>
          <TableRow>
            <TableCell>No errors found</TableCell>
          </TableRow>
        </TableBody>
      )}
    </Table>
  );
};
interface ErrorsTablePaginationProps {
  count: number;
  page: number;
  setPage: (newPage: number) => void;
  rowsPerPageOptions?: number[];
  rowsPerPage: number;
  setRowsPerPage: (newRowsPerPage: number) => void;
  resetPageOnRowsPerPageChange?: boolean;
}

const ErrorsTablePagination: React.FC<ErrorsTablePaginationProps> = ({
  count,
  page,
  setPage,
  rowsPerPageOptions = [10, 25, 50],
  rowsPerPage,
  setRowsPerPage,
  resetPageOnRowsPerPageChange = true,
}) => (
  <TablePagination
    sx={{
      ".MuiTablePagination-spacer": { maxWidth: "35%" },
      ".MuiTablePagination-toolbar": { minHeight: "4em" },
      ".MuiTablePagination-displayedRows": { marginRight: "43px" },
    }}
    rowsPerPageOptions={rowsPerPageOptions}
    count={count}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={(_, newPage) => {
      setPage(newPage);
    }}
    onRowsPerPageChange={({ target: { value } }) => {
      setRowsPerPage(Number(value));
      if (resetPageOnRowsPerPageChange) {
        setPage(0);
      }
    }}
    ActionsComponent={() => (
      <Pagination
        sx={{ ".MuiPagination-ul": { flexWrap: "nowrap" } }}
        color="brandSupporting"
        count={Math.ceil(count / rowsPerPage)}
        page={page + 1}
        onChange={(_, value) => setPage(value - 1)}
      />
    )}
  />
);

function MappingError(props: {
  title: string;
  footer?: string;
  missingFromFormDescription: string;
  missingFromCensusDescription: string;
  missingFromForm: string[];
  missingFromCensus: string[];
  errorColor: string;
}): React.JSX.Element {
  const {
    title,
    footer,
    missingFromFormDescription,
    missingFromCensusDescription,
    missingFromForm,
    missingFromCensus,
    errorColor,
  } = props;
  return (
    <ErrorBox mt={3}>
      <ErrorOutlineIcon sx={{ color: errorColor }} />
      <ErrorInfo>
        <Typography color="inherit">
          <strong>{title}</strong>
        </Typography>
        <MissingClassError
          mt={0.5}
          description={missingFromFormDescription}
          classes={missingFromForm}
        />
        <MissingClassError
          mt={2}
          description={missingFromCensusDescription}
          classes={missingFromCensus}
        />
        {footer ? (
          <Typography
            variant="body-medium"
            color="inherit"
            component="p"
            mt={2}
          >
            {footer}
          </Typography>
        ) : null}
      </ErrorInfo>
    </ErrorBox>
  );
}

interface ExportDataErrorsParams {
  file: File;
  data: ExpandedDataError[];
}

const handleExportDataErrors = async ({
  file,
  data,
}: ExportDataErrorsParams): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  if (file.type === "application/vnd.ms-excel") {
    const xlsWorkbook = XLSX.read(arrayBuffer, { type: "array" });
    for (const sheetName of xlsWorkbook.SheetNames) {
      const xlsSheet = xlsWorkbook.Sheets[sheetName];
      const sheet = workbook.addWorksheet(sheetName);
      const rows = XLSX.utils.sheet_to_json<CellValue[]>(xlsSheet, {
        header: 1,
      });

      for (const [rowIndex, row] of rows.entries()) {
        const newRow = sheet.getRow(rowIndex + 1);
        for (const [colIndex, cellValue] of row.entries()) {
          newRow.getCell(colIndex + 1).value = cellValue;
        }
      }
    }
  } else {
    await workbook.xlsx.load(arrayBuffer);
  }

  const sheet = workbook.worksheets[0];
  if (sheet) {
    for (const item of data) {
      const cell = item.location;
      sheet.getCell(cell).note = item.message;
      sheet.getCell(cell).style = {
        ...(sheet.getCell(cell).style || {}),
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F08080" },
        },
      };
    }

    downloadExcelFile("census_errors.xlsx", workbook);
  }
};

type ExpandedDataError = {
  column: string;
  location: string;
  value: CellValue;
  message: string;
};

const getExpandedDataErrors = (data: DataError[]): ExpandedDataError[] => {
  const response: ExpandedDataError[] = [];
  for (const item of data) {
    for (let index = 0; index < item.location.length; index++) {
      const newError: ExpandedDataError = {
        column: item.column,
        location: item.location[index],
        value: item.values[index],
        message: item.messages[index],
      };
      response.push(newError);
    }
  }
  return response;
};

function MissingClassError({
  description,
  mt,
  classes,
}: {
  description: string;
  mt: number;
  classes: string[];
}): React.JSX.Element | null {
  if (classes.length === 0) return null;

  return (
    <>
      <Typography variant="body-medium" color="inherit" component="p" mt={mt}>
        {description}
      </Typography>
      <Box component="ul" my={0}>
        {classes.map((item: string) => (
          <li key={item}>
            <Typography variant="body-medium" color="inherit">
              <strong>{item}</strong>
            </Typography>
          </li>
        ))}
      </Box>
    </>
  );
}
